import { useState, useEffect, useRef } from "react";

const ADVISOR = {
  name: "Mike Kamal",
  title: "Financial Advisor | WFG",
  tagline: "Building Wealth. Securing Futures. Empowering Communities.",
  firm: "Mike Kamal | WFG",
  bio: "I help individuals, families, and business owners take control of their financial future through personalized strategies, real education, and a supportive community — so you can make confident money decisions at every stage of life.",
  credentials: ["WFG Advisor", "Life & Investments", "Wealth Strategies"],
  contact: "mkamal03pykc@wfgmail.ca",
  phone: "780-691-6504",
};

const NAV_ITEMS = ["Home", "Services", "Education", "Community", "Contact"];

const SERVICE_CATEGORIES = [
  {
    icon: "💰",
    label: "Account Options",
    color: "#4A90D9",
    accent: "rgba(74,144,217,0.12)",
    border: "rgba(74,144,217,0.3)",
    desc: "Build and grow your wealth with the right registered and non-registered accounts tailored to your goals.",
    features: [
      "RRSP – Registered Retirement Savings Plan",
      "RESP – Registered Education Savings Plan",
      "TFSA – Tax-free Savings Account",
      "FHSA – First Home Savings Account",
      "RDSP – Registered Disability Savings Plan",
      "Non-registered Investment Account",
    ],
  },
  {
    icon: "🏢",
    label: "Solutions for Business Owners",
    color: "#C8A96E",
    accent: "rgba(200,169,110,0.12)",
    border: "rgba(200,169,110,0.3)",
    desc: "Comprehensive strategies to protect your business, your employees, and your legacy.",
    features: [
      "Corporate Preferred Retirement Solutions",
      "Corporate Preferred Estate Transfer Solutions",
      "Buy & Sell Agreement",
      "Key Person Business Insurance",
      "Group Living Benefits Insurance",
      "Group Retirement",
      "Business Overhead Expenses Insurance",
      "Business Succession Plan",
      "Mergers & Acquisitions",
      "Capital Acquisition",
    ],
  },
  {
    icon: "🏠",
    label: "Home Financing",
    color: "#5BB8A0",
    accent: "rgba(91,184,160,0.12)",
    border: "rgba(91,184,160,0.3)",
    desc: "Smart financing solutions to help you buy, refinance, or leverage your home's equity.",
    features: [
      "Mortgage",
      "Home Refinancing",
      "Home Equity Line of Credit (HELOC)",
    ],
  },
  {
    icon: "📉",
    label: "Debt Management",
    color: "#E07B6A",
    accent: "rgba(224,123,106,0.12)",
    border: "rgba(224,123,106,0.3)",
    desc: "Take back control of your finances with proven debt reduction and management strategies.",
    features: [
      "Debt Consolidation",
      "Debt Management",
      "Consumer Proposal",
      "Bankruptcy",
    ],
  },
  {
    icon: "🕊️",
    label: "Funeral Planning & Concierge",
    color: "#9B8EC4",
    accent: "rgba(155,142,196,0.12)",
    border: "rgba(155,142,196,0.3)",
    desc: "Thoughtful planning tools and compassionate support so your family is protected and prepared.",
    features: [
      "Funeral Planning Advice",
      "Will Preparation",
      "Online Planning and Storage Tools",
      "24/7 Advisor Planning Assistance",
      "At-Need Family Support/Family Assistance",
    ],
  },
  {
    icon: "🛡️",
    label: "Risk Management Solutions",
    color: "#4A90D9",
    accent: "rgba(74,144,217,0.12)",
    border: "rgba(74,144,217,0.3)",
    desc: "Protect what matters most with comprehensive insurance coverage for every aspect of your life.",
    features: [
      "Auto Insurance",
      "Home Insurance",
      "Commercial Insurance",
      "Travel Insurance",
      "Loan Insurance",
      "Mortgage Insurance",
      "Pet Insurance",
      "Funeral Concierge Services",
    ],
  },
  {
    icon: "📋",
    label: "Financial Planning",
    color: "#C8A96E",
    accent: "rgba(200,169,110,0.12)",
    border: "rgba(200,169,110,0.3)",
    desc: "A complete roadmap for your financial life — from retirement to estate, taxes to education.",
    features: [
      "Retirement Planning",
      "Investment Planning",
      "Tax Planning",
      "Estate Planning and Will",
      "Education Planning",
      "Business Succession Planning",
      "Protection Plan",
    ],
  },
  {
    icon: "🏦",
    label: "Annuity & Life Insurance",
    color: "#5BB8A0",
    accent: "rgba(91,184,160,0.12)",
    border: "rgba(91,184,160,0.3)",
    desc: "Guaranteed income and lifelong protection for you and the people who depend on you.",
    features: [
      "Whole Life Insurance",
      "Universal Life Insurance",
      "Term Insurance",
      "Final Expenses Insurance",
      "Annuities",
    ],
  },
  {
    icon: "📈",
    label: "Wealth Strategies",
    color: "#9B8EC4",
    accent: "rgba(155,142,196,0.12)",
    border: "rgba(155,142,196,0.3)",
    desc: "Sophisticated wealth-building and protection strategies for every income level and stage of life.",
    features: [
      "Managed Portfolios",
      "Private Investment",
      "Wealth Building",
      "Wealth Structuring",
      "Wealth Protection",
      "Wealth Transfer",
    ],
  },
  {
    icon: "💊",
    label: "Living Benefits Insurance",
    color: "#E07B6A",
    accent: "rgba(224,123,106,0.12)",
    border: "rgba(224,123,106,0.3)",
    desc: "Coverage that pays YOU while you're still living — protecting your income and health when it matters most.",
    features: [
      "Disability Insurance",
      "Critical Illness Insurance",
      "Long Term Care",
      "Health and Dental Coverage",
    ],
  },
];

const EDUCATION_CONTENT = [
  { title: "RRSP vs TFSA: Which One Should You Prioritize?", duration: "6 min read", icon: "💰", tag: "Accounts" },
  { title: "How the FHSA Can Help You Buy Your First Home", duration: "5 min read", icon: "🏠", tag: "Home" },
  { title: "Why Every Business Owner Needs Key Person Insurance", duration: "8 min read", icon: "🏢", tag: "Business" },
  { title: "Understanding Term vs. Whole Life Insurance", duration: "7 min read", icon: "🛡️", tag: "Insurance" },
  { title: "How to Build a Retirement Income You Can't Outlive", duration: "9 min read", icon: "📈", tag: "Retirement" },
  { title: "Debt Consolidation: Is It Right for You?", duration: "5 min read", icon: "📉", tag: "Debt" },
];

const COMMUNITY_POSTS = [
  {
    author: "Sandra L.",
    avatar: "SL",
    avatarColor: "#4A90D9",
    time: "2h ago",
    content: "Just opened my FHSA with Mike's help — didn't even know this account existed before! First-time homebuyers, this is a game changer. $8,000/year tax-deductible AND tax-free growth. 🏠🎉",
    likes: 41,
    replies: 14,
    tag: "Win",
  },
  {
    author: "Kevin M.",
    avatar: "KM",
    avatarColor: "#C8A96E",
    time: "4h ago",
    content: "Question: I'm a sole proprietor — should I be looking at a corporate structure for the tax benefits? Mike has mentioned this before but curious if anyone here has made the switch.",
    likes: 22,
    replies: 31,
    tag: "Question",
  },
  {
    author: "Priya T.",
    avatar: "PT",
    avatarColor: "#5BB8A0",
    time: "1d ago",
    content: "Mike's wealth strategy session last week was incredible. Finally have a clear picture of my RRSP, TFSA, and investment accounts all working together. Highly recommend booking a consult!",
    likes: 58,
    replies: 9,
    tag: "Event",
  },
];

const STATS = [
  { value: "500+", label: "Families Served" },
  { value: "10+", label: "Service Areas" },
  { value: "100%", label: "Client Focused" },
  { value: "2,400+", label: "Community Members" },
];

const TESTIMONIALS = [
  {
    name: "Sandra & Kevin M.",
    role: "Family of 4, Edmonton AB",
    text: "Mike helped us set up our RRSPs, TFSAs and got us our first mortgage. He explained everything in plain language — no jargon, just real guidance that actually made sense for our family.",
    avatar: "SK",
    color: "#4A90D9",
  },
  {
    name: "Tyrell B.",
    role: "Restaurant Owner, Calgary AB",
    text: "As a business owner I had zero plan for retirement or protecting my business. Mike set up key person insurance and a corporate retirement strategy. Now I sleep at night knowing I'm covered.",
    avatar: "TB",
    color: "#C8A96E",
  },
  {
    name: "Aisha P.",
    role: "Nurse & First-Gen Investor",
    text: "I never thought investing was 'for me.' Mike walked me through my TFSA and RRSP options with patience and zero judgment. I started my wealth journey within a week of our first call.",
    avatar: "AP",
    color: "#5BB8A0",
  },
];

const EVENTS = [
  { date: "APR 29", title: "Wealth Foundations Webinar", type: "Free Webinar", spots: "42 spots left" },
  { date: "MAY 10", title: "Business Owner Tax Workshop", type: "Workshop", spots: "18 spots left" },
  { date: "MAY 22", title: "Family Finance Night", type: "Community Event", spots: "Open" },
];

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700;900&family=Outfit:wght@300;400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --navy: #0A1628;
    --navy-mid: #0F1E38;
    --navy-light: #162845;
    --navy-card: #1A2F50;
    --blue: #4A90D9;
    --blue-light: #6BAAE8;
    --gold: #C8A96E;
    --gold-light: #E2C98A;
    --teal: #5BB8A0;
    --coral: #E07B6A;
    --lavender: #9B8EC4;
    --cream: #EEF3FA;
    --text: #C8D8ED;
    --text-muted: #6B85A8;
    --border: rgba(74,144,217,0.15);
    --serif: 'Cormorant Garamond', Georgia, serif;
    --sans: 'Outfit', sans-serif;
  }
  body { font-family: var(--sans); background: var(--navy); color: var(--text); line-height: 1.6; }
  .app { min-height: 100vh; overflow-x: hidden; }
  .nav { position: fixed; top: 0; left: 0; right: 0; z-index: 100; background: rgba(10,22,40,0.96); backdrop-filter: blur(20px); border-bottom: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; padding: 0 48px; height: 68px; }
  .nav-brand { display: flex; align-items: center; gap: 12px; }
  .nav-logo { width: 38px; height: 38px; border-radius: 10px; background: linear-gradient(135deg, var(--blue), var(--navy-light)); border: 1.5px solid rgba(74,144,217,0.4); display: flex; align-items: center; justify-content: center; font-family: var(--serif); font-size: 15px; font-weight: 900; color: var(--cream); }
  .nav-name { font-family: var(--serif); font-size: 18px; font-weight: 700; color: var(--cream); }
  .nav-sub { font-size: 10px; color: var(--blue-light); letter-spacing: 0.12em; text-transform: uppercase; margin-top: 1px; }
  .nav-links { display: flex; gap: 4px; }
  .nav-link { background: none; border: none; cursor: pointer; font-family: var(--sans); font-size: 13px; font-weight: 500; color: var(--text-muted); padding: 8px 16px; border-radius: 8px; transition: all 0.2s; }
  .nav-link:hover, .nav-link.active { color: var(--cream); background: rgba(74,144,217,0.1); }
  .nav-cta { background: linear-gradient(135deg, var(--blue), #3A7BC8); color: #fff; border: none; cursor: pointer; font-family: var(--sans); font-size: 13px; font-weight: 600; padding: 10px 22px; border-radius: 10px; transition: all 0.2s; box-shadow: 0 4px 16px rgba(74,144,217,0.3); }
  .nav-cta:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(74,144,217,0.4); }
  .hero { min-height: 100vh; display: flex; align-items: center; padding: 100px 48px 60px; background: radial-gradient(ellipse 70% 60% at 65% 45%, rgba(74,144,217,0.1) 0%, transparent 65%), radial-gradient(ellipse 40% 40% at 5% 85%, rgba(91,184,160,0.07) 0%, transparent 55%), radial-gradient(ellipse 50% 50% at 95% 10%, rgba(200,169,110,0.05) 0%, transparent 50%), var(--navy); position: relative; overflow: hidden; }
  .hero-grid-bg { position: absolute; inset: 0; opacity: 0.025; background-image: linear-gradient(var(--blue) 1px, transparent 1px), linear-gradient(90deg, var(--blue) 1px, transparent 1px); background-size: 60px 60px; }
  .hero-inner { max-width: 1200px; margin: 0 auto; display: grid; grid-template-columns: 1.1fr 0.9fr; gap: 72px; align-items: center; position: relative; z-index: 1; }
  .hero-eyebrow { display: inline-flex; align-items: center; gap: 8px; font-size: 11px; letter-spacing: 0.16em; text-transform: uppercase; color: var(--blue-light); font-weight: 600; margin-bottom: 20px; background: rgba(74,144,217,0.08); border: 1px solid rgba(74,144,217,0.2); padding: 6px 14px; border-radius: 20px; }
  .hero-title { font-family: var(--serif); font-size: clamp(44px, 5.5vw, 72px); font-weight: 900; line-height: 1.06; color: var(--cream); margin-bottom: 24px; }
  .hero-title em { color: var(--gold); font-style: italic; }
  .hero-desc { font-size: 16px; color: var(--text-muted); max-width: 480px; line-height: 1.8; margin-bottom: 40px; }
  .hero-actions { display: flex; gap: 14px; flex-wrap: wrap; margin-bottom: 36px; }
  .btn-primary { background: linear-gradient(135deg, var(--blue), #3A7BC8); color: #fff; border: none; cursor: pointer; font-family: var(--sans); font-size: 15px; font-weight: 600; padding: 14px 30px; border-radius: 12px; transition: all 0.25s; box-shadow: 0 6px 20px rgba(74,144,217,0.3); }
  .btn-primary:hover { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(74,144,217,0.4); }
  .btn-gold { background: linear-gradient(135deg, var(--gold), var(--gold-light)); color: var(--navy); border: none; cursor: pointer; font-family: var(--sans); font-size: 15px; font-weight: 700; padding: 14px 30px; border-radius: 12px; transition: all 0.25s; }
  .btn-gold:hover { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(200,169,110,0.3); }
  .btn-outline { background: transparent; color: var(--blue-light); border: 1.5px solid rgba(74,144,217,0.35); cursor: pointer; font-family: var(--sans); font-size: 15px; font-weight: 500; padding: 14px 30px; border-radius: 12px; transition: all 0.25s; }
  .btn-outline:hover { background: rgba(74,144,217,0.08); border-color: var(--blue); color: var(--cream); }
  .hero-contacts { display: flex; gap: 24px; flex-wrap: wrap; }
  .hero-contact-item { display: flex; align-items: center; gap: 8px; font-size: 13px; color: var(--text-muted); }
  .hero-contact-item a { color: var(--blue-light); text-decoration: none; transition: color 0.2s; }
  .hero-contact-item a:hover { color: var(--cream); }
  .profile-card { background: var(--navy-card); border: 1px solid var(--border); border-radius: 24px; overflow: hidden; box-shadow: 0 24px 64px rgba(0,0,0,0.4); }
  .profile-card-top { background: linear-gradient(145deg, var(--navy-light), var(--navy-mid)); border-bottom: 1px solid var(--border); padding: 28px; position: relative; }
  .profile-card-top::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, var(--blue), var(--teal), var(--gold)); }
  .photo-placeholder { width: 100%; aspect-ratio: 4/3; border-radius: 14px; background: linear-gradient(145deg, rgba(74,144,217,0.08), rgba(74,144,217,0.04)); border: 2px dashed rgba(74,144,217,0.25); display: flex; flex-direction: column; align-items: center; justify-content: center; margin-bottom: 20px; color: var(--text-muted); gap: 10px; max-height: 220px; }
  .photo-placeholder-icon { font-size: 44px; opacity: 0.4; }
  .photo-placeholder-text { font-size: 12px; opacity: 0.5; letter-spacing: 0.08em; text-transform: uppercase; }
  .advisor-name { font-family: var(--serif); font-size: 24px; font-weight: 700; color: var(--cream); margin-bottom: 4px; }
  .advisor-title-tag { font-size: 12px; color: var(--blue-light); letter-spacing: 0.08em; text-transform: uppercase; font-weight: 600; margin-bottom: 16px; }
  .creds { display: flex; gap: 8px; flex-wrap: wrap; }
  .cred-badge { background: rgba(74,144,217,0.1); border: 1px solid rgba(74,144,217,0.22); color: var(--blue-light); font-size: 11px; font-weight: 600; padding: 4px 12px; border-radius: 20px; }
  .profile-stats { display: grid; grid-template-columns: 1fr 1fr; }
  .p-stat { padding: 18px 20px; border-right: 1px solid var(--border); border-bottom: 1px solid var(--border); text-align: center; }
  .p-stat:nth-child(2n) { border-right: none; }
  .p-stat:nth-child(3), .p-stat:nth-child(4) { border-bottom: none; }
  .p-stat-val { font-family: var(--serif); font-size: 26px; font-weight: 900; color: var(--gold); }
  .p-stat-lbl { font-size: 11px; color: var(--text-muted); margin-top: 2px; }
  .section { padding: 96px 48px; max-width: 1200px; margin: 0 auto; }
  .section-full-bg { background: var(--navy-mid); border-top: 1px solid var(--border); border-bottom: 1px solid var(--border); }
  .section-label { display: inline-flex; align-items: center; gap: 6px; font-size: 11px; letter-spacing: 0.16em; text-transform: uppercase; color: var(--blue-light); font-weight: 600; margin-bottom: 14px; }
  .section-title { font-family: var(--serif); font-size: clamp(32px, 4vw, 52px); font-weight: 900; color: var(--cream); margin-bottom: 16px; line-height: 1.12; }
  .section-title em { color: var(--gold); font-style: italic; }
  .section-desc { font-size: 16px; color: var(--text-muted); max-width: 580px; line-height: 1.8; }
  .divider { width: 48px; height: 3px; background: linear-gradient(90deg, var(--blue), var(--teal)); border-radius: 2px; margin: 24px 0; }
  .services-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-top: 52px; }
  .service-card { background: var(--navy); border: 1px solid var(--border); border-radius: 18px; padding: 28px; transition: all 0.3s; cursor: pointer; position: relative; overflow: hidden; }
  .service-card:hover { transform: translateY(-5px); }
  .service-card-accent { position: absolute; top: 0; left: 0; right: 0; height: 3px; }
  .service-icon { font-size: 30px; margin-bottom: 14px; }
  .service-label { font-family: var(--serif); font-size: 18px; font-weight: 700; color: var(--cream); margin-bottom: 10px; line-height: 1.25; }
  .service-desc { font-size: 13px; color: var(--text-muted); line-height: 1.7; margin-bottom: 16px; }
  .service-features { list-style: none; display: flex; flex-direction: column; gap: 5px; }
  .service-features li { font-size: 12px; color: var(--text-muted); display: flex; align-items: flex-start; gap: 7px; line-height: 1.45; }
  .service-features li span.check { font-size: 10px; font-weight: 800; margin-top: 3px; flex-shrink: 0; }
  .services-view-all { text-align: center; margin-top: 36px; }
  .stats-bar { background: linear-gradient(90deg, var(--navy-mid), var(--navy-light), var(--navy-mid)); border-top: 1px solid var(--border); border-bottom: 1px solid var(--border); padding: 44px 48px; }
  .stats-inner { max-width: 1200px; margin: 0 auto; display: grid; grid-template-columns: repeat(4, 1fr); }
  .stat-item { text-align: center; padding: 0 24px; border-right: 1px solid var(--border); }
  .stat-item:last-child { border-right: none; }
  .stat-value { font-family: var(--serif); font-size: 44px; font-weight: 900; color: var(--gold); line-height: 1; }
  .stat-label { font-size: 13px; color: var(--text-muted); margin-top: 6px; }
  .edu-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-top: 52px; }
  .edu-card { background: var(--navy-card); border: 1px solid var(--border); border-radius: 16px; padding: 24px; transition: all 0.25s; cursor: pointer; }
  .edu-card:hover { transform: translateY(-4px); border-color: rgba(74,144,217,0.3); }
  .edu-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 14px; }
  .edu-icon { font-size: 28px; }
  .edu-tag { font-size: 10px; font-weight: 700; padding: 3px 10px; border-radius: 20px; letter-spacing: 0.08em; text-transform: uppercase; background: rgba(74,144,217,0.1); color: var(--blue-light); border: 1px solid rgba(74,144,217,0.2); }
  .edu-title { font-family: var(--serif); font-size: 17px; font-weight: 700; color: var(--cream); margin-bottom: 10px; line-height: 1.35; }
  .edu-meta { font-size: 12px; color: var(--text-muted); }
  .edu-read { margin-top: 14px; background: none; border: none; cursor: pointer; color: var(--blue-light); font-size: 13px; font-weight: 500; padding: 0; font-family: var(--sans); transition: all 0.2s; display: flex; align-items: center; gap: 4px; }
  .edu-card:hover .edu-read { color: var(--blue); gap: 8px; }
  .community-layout { display: grid; grid-template-columns: 2fr 1fr; gap: 40px; margin-top: 52px; }
  .post-card { background: var(--navy-card); border: 1px solid var(--border); border-radius: 16px; padding: 24px; margin-bottom: 16px; transition: border-color 0.2s; }
  .post-card:hover { border-color: rgba(74,144,217,0.25); }
  .post-header { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
  .post-avatar { width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; color: #fff; flex-shrink: 0; }
  .post-author { font-weight: 600; color: var(--cream); font-size: 14px; }
  .post-time { font-size: 11px; color: var(--text-muted); }
  .post-tag { margin-left: auto; font-size: 10px; font-weight: 700; padding: 3px 10px; border-radius: 20px; letter-spacing: 0.06em; text-transform: uppercase; }
  .post-tag.Win { background: rgba(91,184,160,0.12); color: #5BB8A0; border: 1px solid rgba(91,184,160,0.25); }
  .post-tag.Question { background: rgba(155,142,196,0.12); color: #9B8EC4; border: 1px solid rgba(155,142,196,0.25); }
  .post-tag.Event { background: rgba(200,169,110,0.1); color: var(--gold); border: 1px solid rgba(200,169,110,0.2); }
  .post-content { font-size: 14px; color: var(--text-muted); line-height: 1.75; margin-bottom: 14px; }
  .post-actions { display: flex; gap: 20px; }
  .post-action { background: none; border: none; cursor: pointer; color: var(--text-muted); font-size: 12px; display: flex; align-items: center; gap: 5px; font-family: var(--sans); transition: color 0.2s; padding: 0; }
  .post-action:hover { color: var(--blue-light); }
  .sidebar-card { background: var(--navy-card); border: 1px solid var(--border); border-radius: 16px; padding: 26px; margin-bottom: 20px; }
  .sidebar-title { font-family: var(--serif); font-size: 18px; font-weight: 700; color: var(--cream); margin-bottom: 20px; }
  .event-item { padding: 14px 0; border-bottom: 1px solid var(--border); }
  .event-item:last-child { border-bottom: none; padding-bottom: 0; }
  .event-date { font-size: 10px; font-weight: 700; color: var(--gold); letter-spacing: 0.14em; margin-bottom: 4px; }
  .event-title-text { font-size: 14px; font-weight: 600; color: var(--cream); margin-bottom: 4px; }
  .event-meta { display: flex; gap: 8px; align-items: center; }
  .event-type { font-size: 11px; color: var(--text-muted); }
  .event-spots { font-size: 11px; color: #5BB8A0; font-weight: 600; }
  .join-box { background: linear-gradient(135deg, rgba(74,144,217,0.08), rgba(91,184,160,0.05)); border: 1px solid rgba(74,144,217,0.2); border-radius: 14px; padding: 26px; text-align: center; }
  .join-box h4 { font-family: var(--serif); font-size: 18px; color: var(--cream); margin-bottom: 8px; }
  .join-box p { font-size: 13px; color: var(--text-muted); margin-bottom: 18px; line-height: 1.6; }
  .join-input { width: 100%; background: rgba(255,255,255,0.04); border: 1px solid var(--border); border-radius: 8px; padding: 10px 14px; font-family: var(--sans); font-size: 14px; color: var(--text); outline: none; margin-bottom: 10px; }
  .join-input:focus { border-color: var(--blue); }
  .join-input::placeholder { color: var(--text-muted); }
  .btn-join { width: 100%; background: linear-gradient(135deg, var(--blue), #3A7BC8); color: #fff; border: none; cursor: pointer; font-family: var(--sans); font-size: 14px; font-weight: 700; padding: 12px; border-radius: 8px; transition: all 0.2s; }
  .btn-join:hover { opacity: 0.9; transform: translateY(-1px); }
  .test-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px; margin-top: 52px; }
  .test-card { background: var(--navy-card); border: 1px solid var(--border); border-radius: 18px; padding: 28px; transition: all 0.25s; }
  .test-card:hover { transform: translateY(-4px); border-color: rgba(74,144,217,0.2); }
  .test-quote { font-size: 40px; color: var(--blue); opacity: 0.35; line-height: 1; margin-bottom: 12px; font-family: serif; }
  .test-text { font-size: 14px; color: var(--text-muted); line-height: 1.8; margin-bottom: 20px; font-style: italic; }
  .test-author { display: flex; align-items: center; gap: 12px; }
  .test-avatar { width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; color: #fff; }
  .test-name { font-weight: 600; color: var(--cream); font-size: 14px; }
  .test-role { font-size: 12px; color: var(--text-muted); }
  .cta-section { background: linear-gradient(135deg, rgba(74,144,217,0.1), rgba(91,184,160,0.06) 50%, rgba(200,169,110,0.08)); border-top: 1px solid var(--border); border-bottom: 1px solid var(--border); padding: 100px 48px; text-align: center; }
  .cta-inner { max-width: 660px; margin: 0 auto; }
  .cta-title { font-family: var(--serif); font-size: clamp(32px, 4.5vw, 52px); font-weight: 900; color: var(--cream); margin-bottom: 16px; }
  .cta-title em { color: var(--gold); font-style: italic; }
  .cta-desc { font-size: 16px; color: var(--text-muted); line-height: 1.8; margin-bottom: 40px; }
  .cta-actions { display: flex; gap: 16px; justify-content: center; flex-wrap: wrap; }
  .footer { background: #060F1E; border-top: 1px solid var(--border); padding: 64px 48px 32px; }
  .footer-inner { max-width: 1200px; margin: 0 auto; }
  .footer-grid { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; gap: 48px; margin-bottom: 48px; }
  .footer-brand-name { font-family: var(--serif); font-size: 22px; font-weight: 700; color: var(--cream); margin-bottom: 12px; }
  .footer-brand-desc { font-size: 13px; color: var(--text-muted); line-height: 1.75; max-width: 280px; margin-bottom: 16px; }
  .footer-contact-row { display: flex; align-items: center; gap: 8px; font-size: 12px; color: var(--text-muted); margin-bottom: 6px; }
  .footer-contact-row a { color: var(--blue-light); text-decoration: none; }
  .footer-heading { font-size: 11px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--blue-light); font-weight: 700; margin-bottom: 16px; }
  .footer-links { list-style: none; display: flex; flex-direction: column; gap: 10px; }
  .footer-links li { font-size: 13px; color: var(--text-muted); cursor: pointer; transition: color 0.2s; }
  .footer-links li:hover { color: var(--blue-light); }
  .footer-bottom { border-top: 1px solid var(--border); padding-top: 28px; display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; }
  .footer-copy { font-size: 12px; color: var(--text-muted); }
  .footer-disclaimer { font-size: 11px; color: rgba(107,133,168,0.5); max-width: 500px; text-align: right; line-height: 1.6; }
  .modal-overlay { position: fixed; inset: 0; background: rgba(6,15,30,0.9); backdrop-filter: blur(10px); z-index: 200; display: flex; align-items: center; justify-content: center; padding: 20px; }
  .modal { background: var(--navy-light); border: 1px solid var(--border); border-radius: 24px; padding: 40px; max-width: 520px; width: 100%; position: relative; animation: slideUp 0.3s ease; max-height: 90vh; overflow-y: auto; }
  @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
  .modal-close { position: absolute; top: 16px; right: 16px; background: rgba(255,255,255,0.05); border: 1px solid var(--border); border-radius: 8px; width: 34px; height: 34px; cursor: pointer; color: var(--text-muted); font-size: 20px; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
  .modal-close:hover { background: rgba(74,144,217,0.1); color: var(--blue-light); }
  .modal-eyebrow { font-size: 11px; letter-spacing: 0.14em; text-transform: uppercase; color: var(--blue-light); font-weight: 700; margin-bottom: 8px; }
  .modal-title { font-family: var(--serif); font-size: 28px; font-weight: 900; color: var(--cream); margin-bottom: 8px; }
  .modal-sub { font-size: 14px; color: var(--text-muted); margin-bottom: 28px; line-height: 1.6; }
  .form-group { margin-bottom: 16px; }
  .form-label { font-size: 11px; font-weight: 700; color: var(--text-muted); letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 8px; display: block; }
  .form-input { width: 100%; background: rgba(255,255,255,0.04); border: 1px solid var(--border); border-radius: 10px; padding: 12px 14px; font-family: var(--sans); font-size: 14px; color: var(--text); outline: none; transition: border-color 0.2s; }
  .form-input:focus { border-color: var(--blue); }
  .form-input::placeholder { color: var(--text-muted); }
  .form-select { width: 100%; background: var(--navy); border: 1px solid var(--border); border-radius: 10px; padding: 12px 14px; font-family: var(--sans); font-size: 14px; color: var(--text); outline: none; cursor: pointer; }
  .btn-submit { width: 100%; background: linear-gradient(135deg, var(--blue), #3A7BC8); color: #fff; border: none; cursor: pointer; font-family: var(--sans); font-size: 15px; font-weight: 700; padding: 14px; border-radius: 12px; transition: all 0.2s; margin-top: 8px; }
  .btn-submit:hover { opacity: 0.95; transform: translateY(-1px); }
  .chat-fab { position: fixed; bottom: 32px; right: 32px; z-index: 150; width: 58px; height: 58px; border-radius: 50%; background: linear-gradient(135deg, var(--blue), #3A7BC8); border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 22px; box-shadow: 0 8px 32px rgba(74,144,217,0.4); transition: all 0.25s; }
  .chat-fab:hover { transform: scale(1.1); }
  .chat-window { position: fixed; bottom: 108px; right: 32px; z-index: 150; width: 370px; background: var(--navy-light); border: 1px solid var(--border); border-radius: 22px; overflow: hidden; animation: slideUp 0.25s ease; box-shadow: 0 24px 64px rgba(0,0,0,0.5); }
  .chat-header { background: linear-gradient(135deg, var(--navy-mid), var(--navy-light)); border-bottom: 1px solid var(--border); padding: 16px 20px; display: flex; align-items: center; gap: 12px; }
  .chat-header-icon { font-size: 26px; }
  .chat-header-name { font-weight: 700; color: var(--cream); font-size: 14px; }
  .chat-header-status { font-size: 11px; color: #5BB8A0; margin-top: 1px; }
  .chat-header-close { margin-left: auto; background: none; border: none; cursor: pointer; color: var(--text-muted); font-size: 20px; line-height: 1; }
  .chat-messages { height: 270px; overflow-y: auto; padding: 16px; display: flex; flex-direction: column; gap: 12px; }
  .chat-msg { max-width: 82%; font-size: 13px; line-height: 1.65; padding: 10px 14px; border-radius: 14px; }
  .chat-msg.bot { background: rgba(255,255,255,0.05); border: 1px solid var(--border); color: var(--text); align-self: flex-start; border-bottom-left-radius: 4px; }
  .chat-msg.user { background: linear-gradient(135deg, var(--blue), #3A7BC8); color: #fff; font-weight: 500; align-self: flex-end; border-bottom-right-radius: 4px; }
  .chat-input-row { display: flex; gap: 8px; padding: 12px 16px; border-top: 1px solid var(--border); }
  .chat-input { flex: 1; background: rgba(255,255,255,0.05); border: 1px solid var(--border); border-radius: 10px; padding: 9px 12px; font-family: var(--sans); font-size: 13px; color: var(--text); outline: none; }
  .chat-input:focus { border-color: var(--blue); }
  .chat-input::placeholder { color: var(--text-muted); }
  .chat-send { background: linear-gradient(135deg, var(--blue), #3A7BC8); color: #fff; border: none; cursor: pointer; border-radius: 10px; padding: 9px 16px; font-size: 16px; transition: opacity 0.2s; }
  .chat-send:hover { opacity: 0.9; }
  @media (max-width: 960px) {
    .hero-inner, .services-grid, .edu-grid, .test-grid, .community-layout, .footer-grid { grid-template-columns: 1fr; }
    .stats-inner { grid-template-columns: 1fr 1fr; }
    .stat-item { border-right: none; border-bottom: 1px solid var(--border); padding: 20px; }
    .stat-item:last-child { border-bottom: none; }
    .nav-links { display: none; }
    .hero, .cta-section { padding: 100px 20px 60px; }
    .section { padding: 64px 20px; }
    .footer { padding: 48px 20px 24px; }
    .stats-bar { padding: 32px 20px; }
  }
`;

export default function FinancialAdvisorApp() {
  const [activeNav, setActiveNav] = useState("Home");
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("consult");
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    { role: "bot", text: "Hi! I'm Mike Kamal's AI assistant. I can answer your questions about RRSPs, TFSAs, life insurance, wealth strategies, and more. What's on your mind?" },
  ]);
  const [chatInput, setChatInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [joinEmail, setJoinEmail] = useState("");
  const [joinSuccess, setJoinSuccess] = useState(false);
  const [showAllServices, setShowAllServices] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages, isTyping]);

  const openModal = (type) => { setModalType(type); setShowModal(true); };

  const sendChat = async () => {
    if (!chatInput.trim()) return;
    const userMsg = chatInput.trim();
    setChatInput("");
    setChatMessages(prev => [...prev, { role: "user", text: userMsg }]);
    setIsTyping(true);
    try {
      const response = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          system: `You are a knowledgeable and friendly AI financial assistant for Mike Kamal, a WFG Financial Advisor based in Alberta, Canada. Mike offers: Account Options (RRSP, RESP, TFSA, FHSA, RDSP, non-registered), Risk Management (auto/home/commercial/travel/loan/mortgage/pet insurance), Financial Planning (retirement, investment, tax, estate, education, business succession), Annuity & Life Insurance (whole life, universal life, term, final expenses, annuities), Wealth Strategies (managed portfolios, private investment, wealth building/structuring/protection/transfer), Living Benefits (disability, critical illness, long-term care, health & dental), Home Financing (mortgage, refinancing, HELOC), Debt Management (consolidation, consumer proposal, bankruptcy), Business Owner Solutions (corporate retirement, estate transfer, buy/sell, key person, group benefits, succession, M&A), and Funeral Planning & Concierge. Keep answers concise (2-4 sentences), friendly, educational, and Canada-focused. Always suggest booking a free consultation with Mike at mkamal03pykc@wfgmail.ca or 780-691-6504 for personalized advice. Never give specific investment recommendations.`,
          messages: [{ role: "user", content: userMsg }],
        }),
      });
      const data = await response.json();
      const reply = data.content?.map(b => b.text || "").join("") || "Great question! For personalized guidance, I'd recommend booking a free consult with Mike at 780-691-6504.";
      setChatMessages(prev => [...prev, { role: "bot", text: reply }]);
    } catch {
      setChatMessages(prev => [...prev, { role: "bot", text: "Something went wrong. Reach Mike directly at 780-691-6504 or mkamal03pykc@wfgmail.ca." }]);
    }
    setIsTyping(false);
  };

  const handleJoin = () => {
    if (joinEmail.includes("@")) { setJoinSuccess(true); setJoinEmail(""); }
  };

  const visibleServices = showAllServices ? SERVICE_CATEGORIES : SERVICE_CATEGORIES.slice(0, 6);

  return (
    <>
      <style>{styles}</style>
      <div className="app">
        <nav className="nav">
          <div className="nav-brand">
            <div className="nav-logo">MK</div>
            <div>
              <div className="nav-name">Mike Kamal</div>
              <div className="nav-sub">WFG Financial Advisor</div>
            </div>
          </div>
          <div className="nav-links">
            {NAV_ITEMS.map(item => (
              <button key={item} className={`nav-link ${activeNav === item ? "active" : ""}`} onClick={() => setActiveNav(item)}>{item}</button>
            ))}
          </div>
          <button className="nav-cta" onClick={() => openModal("consult")}>📅 Book Free Consult</button>
        </nav>

        <section className="hero">
          <div className="hero-grid-bg" />
          <div className="hero-inner">
            <div>
              <div className="hero-eyebrow">🇨🇦 WFG Financial Advisor · Alberta, Canada</div>
              <h1 className="hero-title">Your Path to Financial <em>Freedom</em> Starts Here</h1>
              <p className="hero-desc">{ADVISOR.bio}</p>
              <div className="hero-actions">
                <button className="btn-primary" onClick={() => openModal("consult")}>📅 Book Free Consultation</button>
                <button className="btn-gold" onClick={() => setShowChat(true)}>💬 Ask AI Assistant</button>
                <button className="btn-outline" onClick={() => setActiveNav("Community")}>Join Community</button>
              </div>
              <div className="hero-contacts">
                <div className="hero-contact-item">📞 <a href={`tel:${ADVISOR.phone}`}>{ADVISOR.phone}</a></div>
                <div className="hero-contact-item">✉️ <a href={`mailto:${ADVISOR.contact}`}>{ADVISOR.contact}</a></div>
              </div>
            </div>
            <div className="profile-card">
              <div className="profile-card-top">
                <div className="photo-placeholder">
                  <div className="photo-placeholder-icon">📷</div>
                  <div className="photo-placeholder-text">Photo Coming Soon</div>
                </div>
                <div className="advisor-name">{ADVISOR.name}</div>
                <div className="advisor-title-tag">{ADVISOR.title}</div>
                <div className="creds">
                  {ADVISOR.credentials.map(c => <span key={c} className="cred-badge">{c}</span>)}
                </div>
              </div>
              <div className="profile-stats">
                {STATS.map(s => (
                  <div key={s.label} className="p-stat">
                    <div className="p-stat-val">{s.value}</div>
                    <div className="p-stat-lbl">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <div className="stats-bar">
          <div className="stats-inner">
            {STATS.map(s => (
              <div key={s.label} className="stat-item">
                <div className="stat-value">{s.value}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="section-full-bg">
          <div className="section">
            <div className="section-label">⬡ Full-Spectrum Services</div>
            <h2 className="section-title">Everything You Need,<br /><em>Under One Roof</em></h2>
            <div className="divider" />
            <p className="section-desc">From RRSPs and insurance to business succession and debt management — I bring the complete WFG platform to you with personalized guidance every step of the way.</p>
            <div className="services-grid">
              {visibleServices.map((s, i) => (
                <div key={i} className="service-card" onClick={() => openModal("consult")}
                  onMouseEnter={e => e.currentTarget.style.borderColor = s.border}
                  onMouseLeave={e => e.currentTarget.style.borderColor = ""}>
                  <div className="service-card-accent" style={{ background: `linear-gradient(90deg, ${s.color}, transparent)` }} />
                  <div className="service-icon">{s.icon}</div>
                  <div className="service-label" style={{ color: s.color }}>{s.label}</div>
                  <p className="service-desc">{s.desc}</p>
                  <ul className="service-features">
                    {s.features.map((f, j) => (
                      <li key={j}><span className="check" style={{ color: s.color }}>✓</span>{f}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="services-view-all">
              <button className="btn-outline" onClick={() => setShowAllServices(!showAllServices)}>
                {showAllServices ? "Show Less ↑" : `View All ${SERVICE_CATEGORIES.length} Service Areas →`}
              </button>
            </div>
          </div>
        </div>

        <div className="section">
          <div className="section-label">📚 Free Resources</div>
          <h2 className="section-title">Financial Education <em>Centre</em></h2>
          <div className="divider" />
          <p className="section-desc">Plain-language guides on Canadian financial topics that matter most to you, your family, and your business.</p>
          <div className="edu-grid">
            {EDUCATION_CONTENT.map((e, i) => (
              <div key={i} className="edu-card">
                <div className="edu-top">
                  <div className="edu-icon">{e.icon}</div>
                  <span className="edu-tag">{e.tag}</span>
                </div>
                <div className="edu-title">{e.title}</div>
                <div className="edu-meta">📖 {e.duration}</div>
                <button className="edu-read">Read more →</button>
              </div>
            ))}
          </div>
        </div>

        <div className="section-full-bg">
          <div className="section">
            <div className="section-label">🤝 Community</div>
            <h2 className="section-title">Join <em>2,400+ Members</em><br />Building Wealth Together</h2>
            <div className="divider" />
            <p className="section-desc">Real conversations about money — wins, questions, live workshops, and accountability from people on the same journey.</p>
            <div className="community-layout">
              <div>
                {COMMUNITY_POSTS.map((p, i) => (
                  <div key={i} className="post-card">
                    <div className="post-header">
                      <div className="post-avatar" style={{ background: p.avatarColor }}>{p.avatar}</div>
                      <div>
                        <div className="post-author">{p.author}</div>
                        <div className="post-time">{p.time}</div>
                      </div>
                      <span className={`post-tag ${p.tag}`}>{p.tag}</span>
                    </div>
                    <div className="post-content">{p.content}</div>
                    <div className="post-actions">
                      <button className="post-action">❤️ {p.likes}</button>
                      <button className="post-action">💬 {p.replies} replies</button>
                      <button className="post-action">↗️ Share</button>
                    </div>
                  </div>
                ))}
              </div>
              <div>
                <div className="sidebar-card">
                  <div className="sidebar-title">📅 Upcoming Events</div>
                  {EVENTS.map((ev, i) => (
                    <div key={i} className="event-item">
                      <div className="event-date">{ev.date}</div>
                      <div className="event-title-text">{ev.title}</div>
                      <div className="event-meta">
                        <span className="event-type">{ev.type}</span>
                        <span style={{ color: "var(--text-muted)" }}>·</span>
                        <span className="event-spots">{ev.spots}</span>
                      </div>
                    </div>
                  ))}
                  <button className="btn-primary" style={{ width: "100%", marginTop: 20, fontSize: 13 }} onClick={() => openModal("events")}>View All Events</button>
                </div>
                <div className="join-box">
                  <h4>Join the Community</h4>
                  <p>Weekly tips, free workshops, and a network of Canadians building wealth together.</p>
                  {joinSuccess ? (
                    <div style={{ color: "#5BB8A0", fontWeight: 600, fontSize: 14 }}>✅ Welcome to the community!</div>
                  ) : (
                    <>
                      <input className="join-input" placeholder="Your email address" value={joinEmail} onChange={e => setJoinEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && handleJoin()} />
                      <button className="btn-join" onClick={handleJoin}>Join Free →</button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="section">
          <div className="section-label">⭐ Success Stories</div>
          <h2 className="section-title">Real Clients. <em>Real Results.</em></h2>
          <div className="divider" />
          <div className="test-grid">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="test-card">
                <div className="test-quote">"</div>
                <p className="test-text">{t.text}</p>
                <div className="test-author">
                  <div className="test-avatar" style={{ background: t.color }}>{t.avatar}</div>
                  <div>
                    <div className="test-name">{t.name}</div>
                    <div className="test-role">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="cta-section">
          <div className="cta-inner">
            <div className="section-label" style={{ justifyContent: "center" }}>✦ Take the First Step</div>
            <h2 className="cta-title">Ready to <em>Transform</em> Your Financial Future?</h2>
            <p className="cta-desc">Book a free 30-minute discovery call with Mike. No pressure, no jargon — just an honest conversation about where you are and where you want to be.</p>
            <div className="cta-actions">
              <button className="btn-primary" onClick={() => openModal("consult")} style={{ fontSize: 16, padding: "16px 36px" }}>📅 Book Free Consultation</button>
              <button className="btn-gold" onClick={() => setShowChat(true)} style={{ fontSize: 16, padding: "16px 36px" }}>💬 Ask AI Assistant</button>
            </div>
            <div style={{ marginTop: 28, display: "flex", justifyContent: "center", gap: 32, flexWrap: "wrap" }}>
              <div style={{ fontSize: 14, color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 8 }}>
                📞 <a href="tel:780-691-6504" style={{ color: "var(--blue-light)", textDecoration: "none" }}>780-691-6504</a>
              </div>
              <div style={{ fontSize: 14, color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 8 }}>
                ✉️ <a href="mailto:mkamal03pykc@wfgmail.ca" style={{ color: "var(--blue-light)", textDecoration: "none" }}>mkamal03pykc@wfgmail.ca</a>
              </div>
            </div>
          </div>
        </div>

        <footer className="footer">
          <div className="footer-inner">
            <div className="footer-grid">
              <div>
                <div className="footer-brand-name">Mike Kamal | WFG</div>
                <p className="footer-brand-desc">Empowering Canadians — individuals, families, and business owners — with the strategies, education, and community to achieve lasting financial freedom.</p>
                <div className="footer-contact-row">📞 <a href="tel:780-691-6504">780-691-6504</a></div>
                <div className="footer-contact-row">✉️ <a href="mailto:mkamal03pykc@wfgmail.ca">mkamal03pykc@wfgmail.ca</a></div>
              </div>
              <div>
                <div className="footer-heading">Accounts & Planning</div>
                <ul className="footer-links">
                  {["RRSP / TFSA / RESP", "FHSA / RDSP", "Retirement Planning", "Tax Planning", "Estate Planning", "Education Planning"].map(l => <li key={l}>{l}</li>)}
                </ul>
              </div>
              <div>
                <div className="footer-heading">Insurance & Wealth</div>
                <ul className="footer-links">
                  {["Life Insurance", "Living Benefits", "Risk Management", "Wealth Strategies", "Annuities", "Business Solutions"].map(l => <li key={l}>{l}</li>)}
                </ul>
              </div>
              <div>
                <div className="footer-heading">Connect</div>
                <ul className="footer-links">
                  {["Book Consultation", "Join Community", "Free Resources", "Webinars & Events", "LinkedIn", "Instagram"].map(l => <li key={l}>{l}</li>)}
                </ul>
              </div>
            </div>
            <div className="footer-bottom">
              <div className="footer-copy">© 2025 Mike Kamal | WFG. All rights reserved.</div>
              <div className="footer-disclaimer">Financial services provided through World Financial Group. This platform is for educational and informational purposes only. Always consult a licensed advisor for personalized financial advice.</div>
            </div>
          </div>
        </footer>

        {showModal && (
          <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
            <div className="modal">
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
              {modalType === "consult" ? (
                <>
                  <div className="modal-eyebrow">Free 30-Min Discovery Call</div>
                  <div className="modal-title">Book Your Free Consultation</div>
                  <div className="modal-sub">No pressure. No jargon. Just an honest conversation about your financial goals with Mike Kamal.</div>
                  <div className="form-group"><label className="form-label">Full Name</label><input className="form-input" placeholder="Jane Smith" /></div>
                  <div className="form-group"><label className="form-label">Email</label><input className="form-input" placeholder="jane@email.com" /></div>
                  <div className="form-group"><label className="form-label">Phone</label><input className="form-input" placeholder="(780) 000-0000" /></div>
                  <div className="form-group">
                    <label className="form-label">I am a...</label>
                    <select className="form-select">
                      <option>Individual / Family</option>
                      <option>Business Owner</option>
                      <option>First-Time Investor</option>
                      <option>Pre-Retiree</option>
                      <option>New Canadian</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">I'm most interested in...</label>
                    <select className="form-select">
                      <option>RRSP / TFSA / RESP / FHSA</option>
                      <option>Life / Disability Insurance</option>
                      <option>Retirement Planning</option>
                      <option>Debt Management</option>
                      <option>Wealth Strategies</option>
                      <option>Business Solutions</option>
                      <option>Home Financing</option>
                      <option>Full Financial Review</option>
                    </select>
                  </div>
                  <div className="form-group"><label className="form-label">What's your biggest financial goal?</label><input className="form-input" placeholder="e.g., retire by 60, buy a home, protect my family..." /></div>
                  <button className="btn-submit" onClick={() => setShowModal(false)}>📅 Request My Free Consultation</button>
                </>
              ) : (
                <>
                  <div className="modal-eyebrow">Live & Online</div>
                  <div className="modal-title">Upcoming Events</div>
                  <div className="modal-sub">Join Mike live — free webinars, workshops, and community nights for all levels.</div>
                  {EVENTS.map((ev, i) => (
                    <div key={i} style={{ padding: "16px 0", borderBottom: "1px solid var(--border)" }}>
                      <div style={{ color: "var(--gold)", fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 4 }}>{ev.date} · {ev.type}</div>
                      <div style={{ color: "var(--cream)", fontWeight: 600, marginBottom: 4 }}>{ev.title}</div>
                      <div style={{ color: "#5BB8A0", fontSize: 12, fontWeight: 600 }}>{ev.spots}</div>
                    </div>
                  ))}
                  <button className="btn-submit" style={{ marginTop: 20 }} onClick={() => setShowModal(false)}>Register for an Event</button>
                </>
              )}
            </div>
          </div>
        )}

        {showChat && (
          <div className="chat-window">
            <div className="chat-header">
              <div className="chat-header-icon">🤖</div>
              <div>
                <div className="chat-header-name">Mike's AI Assistant</div>
                <div className="chat-header-status">● Online · Ask me anything financial</div>
              </div>
              <button className="chat-header-close" onClick={() => setShowChat(false)}>×</button>
            </div>
            <div className="chat-messages">
              {chatMessages.map((m, i) => <div key={i} className={`chat-msg ${m.role}`}>{m.text}</div>)}
              {isTyping && <div className="chat-msg bot" style={{ opacity: 0.6 }}>Thinking…</div>}
              <div ref={chatEndRef} />
            </div>
            <div className="chat-input-row">
              <input className="chat-input" placeholder="Ask about RRSP, insurance, debt..." value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => e.key === "Enter" && sendChat()} />
              <button className="chat-send" onClick={sendChat}>→</button>
            </div>
          </div>
        )}
        <button className="chat-fab" onClick={() => setShowChat(!showChat)} title="Ask AI Assistant">
          {showChat ? "×" : "💬"}
        </button>

      </div>
    </>
  );
}